import { downloadBlob } from '@mint-lib/api-zod-support';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import { Chat, KnowledgeBase } from '@mint-sdk/api-ai-assistant';

import { useDeleteDocumentMutation } from '../../queries/rag.js';
import type { RagTableType } from '../../types/rag.js';

type DocumentRef = Pick<RagTableType, 'fileId' | 'fileName'>;

const onDocumentDownload = async ({ fileId, fileName }: DocumentRef) => {
  const blob = await Chat.getRagDocument({ urlParams: { fileId } });

  downloadBlob(blob, fileName);
};

export const useDocumentActions = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const queryClient = useQueryClient();
  const { mutateAsync: deleteDocument } = useDeleteDocumentMutation();

  // Downloads start in parallel; the selection clears without waiting on them,
  // and the ones that fail are counted up once they have all settled.
  const onDocumentsBulkDownload = (
    documents: DocumentRef[],
    onDownload: () => void,
  ) => {
    void Promise.allSettled(documents.map(onDocumentDownload)).then(
      (results) => {
        const failed = results.filter(
          ({ status }) => status === 'rejected',
        ).length;

        if (failed) {
          enqueueSnackbar({
            variant: 'error',
            subTitle: t('{{count}} file could not be downloaded', {
              count: failed,
            }),
            removable: true,
          });
        }
      },
    );

    onDownload();
  };

  /**
   * Deletes run in parallel; the lists refresh once they have all settled, and
   * a refusal is reported rather than swallowed.
   */
  const onDocumentsBulkDelete = async (documents: DocumentRef[]) => {
    const results = await Promise.allSettled(
      documents.map(({ fileId }) => deleteDocument(fileId)),
    );

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: Chat.listRagDocuments({}).queryKey,
      }),
      KnowledgeBase.getKnowledgeBaseListing.invalidateAll(queryClient, {}),
    ]);

    const failed = results.filter(({ status }) => status === 'rejected').length;

    if (failed) {
      enqueueSnackbar({
        variant: 'error',
        subTitle: t('{{count}} file could not be deleted', { count: failed }),
        removable: true,
      });
    }
  };

  return {
    onDocumentDownload,
    onDocumentsBulkDownload,
    onDocumentsBulkDelete,
  };
};
